/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FontWeights, Toggle } from '@fluentui/react';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { FormValidators } from 'src/core/utils/formValidators';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import { AzureMLTabConnectionFooter } from 'src/customText/verticals/azureMLConnectionModal/components/AzureMLTabConnectionFooter';
import styled from 'styled-components';

type OutsourcingPageProps = {
    onNext: (amlConnectionInfo: Partial<AMLConnectionInfo>) => void;
    onDismiss: () => void;
    onPrevious: () => void;
    amlConnectionInfo: AMLConnectionInfo;
};

const messages = defineMessages({
    disclaimer: {
        id: 'customText.outsourcingPage.disclaimer',
        defaultMessage: 'Disclaimer:'
    },
    disclaimerContent: {
        id: 'customText.outsourcingPage.disclaimerContent',
        defaultMessage: `You are responsible for any decision to use a labeling company that offers services through Azure Marketplace. It is recommended that you independently assess whether a labeling company and its experience, services, staffing, terms, and so on will meet your project requirements. You, and not Microsoft, are responsible for all aspects of your engagement with a labeling company, including but not limited to issues relating to scope, quality, schedule, and pricing.`
    },
    title: {
        id: 'customText.outsourcingPage.title',
        defaultMessage: 'Outsource labeling to a vendor (optional)'
    },
    toggleDescription: {
        id: 'customText.outsourcingPage.toggleDescription',
        defaultMessage: 'Use a vendor labeling company from Azure Marketplace'
    },
    choosingVendor: {
        id: 'customText.outsourcingPage.choosingVendor',
        defaultMessage: 'Choosing a vendor:'
    },
    description1: {
        id: 'customText.outsourcingPage.description1',
        defaultMessage: 'Before you choose a vendor, contact the vendor labeling companies on'
    },
    extLink1: {
        id: 'customText.outsourcingPage.extLink1',
        defaultMessage: 'Azure Marketplace'
    },
    description2: {
        id: 'customText.outsourcingPage.description2',
        defaultMessage: 'and finalize a contract with them. Learn more about using'
    },
    extLink2: {
        id: 'customText.outsourcingPage.extLink2',
        defaultMessage: 'labeling vendor companies.'
    },
    vendorPlaceholder: {
        id: 'customText.outsourcingPage.vendorPlaceholder',
        defaultMessage: 'Choose a vendor'
    },
    emptyItemPlaceholder: {
        id: 'customText.outsourcingPage.emptyItemPlaceholder',
        defaultMessage: 'No vendors found:'
    }
});

const vendorsOption = [
    { key: 'iSoftStone Inc', text: 'iSoftStone Inc' },
    { key: 'Quadrant Resource', text: 'Quadrant Resource' }
];

const StyledDropdown = styled(LuisDropdown)<{ rootWidth: string }>(props => ({
    width: props.rootWidth
}));

export const OutsourcingPage = (props: OutsourcingPageProps) => {
    const { onDismiss, onNext, onPrevious } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();

    const { formState, updateFormState } = useFormState<Partial<AMLConnectionInfo>>({
        outsourcing: [false, FormValidators.createRequiredValidator()],
        vendor: ['', FormValidators.createRequiredValidator()]
    });

    const handleNext = () => onNext({ outsourcing: formState.fields.outsourcing.value, vendor: formState.fields.vendor.value });

    return (
        <Stack>
            <MessageBar>
                <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.disclaimer)}</LuisText>
                &nbsp;
                {localize(messages.disclaimerContent)}
            </MessageBar>
            <br />
            <Stack
                styles={{ root: { minHeight: '400px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 12, padding: `${isLargeOrGreater ? `0 0 0 32px` : '12px 20px'}` }}>
                <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.title)}
                </LuisText>

                <Toggle
                    label={localize(messages.toggleDescription)}
                    onText="On"
                    offText="Off"
                    checked={formState.fields.outsourcing.value}
                    onChange={(_, val) => updateFormState({ outsourcing: val })}
                />
                {formState.fields.outsourcing.value ? (
                    <Stack tokens={{ childrenGap: 10 }}>
                        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.choosingVendor)}</LuisText>
                        <LuisText>
                            <LuisText> {localize(messages.description1)} </LuisText>
                            &nbsp;
                            <ExternalLink
                                href={`https://azuremarketplace.microsoft.com/marketplace/consulting-services?search=AzureMLVend`}>
                                {localize(messages.extLink1)}
                            </ExternalLink>
                            &nbsp;
                            <LuisText> {localize(messages.description2)} </LuisText>
                            &nbsp;
                            <ExternalLink href={`https://aka.ms/data-labeling-vendors`}>{localize(messages.extLink2)}</ExternalLink>
                        </LuisText>

                        <StyledDropdown
                            options={vendorsOption}
                            selectedKey={formState.fields.vendor.value}
                            rootWidth={isLargeOrGreater ? '300px' : '90%'}
                            onChange={(_, val) => updateFormState({ vendor: val.key as string })}
                            placeholder={localize(messages.vendorPlaceholder)}
                            emptyStatePlaceholder={localize(messages.emptyItemPlaceholder)}
                        />
                    </Stack>
                ) : null}
            </Stack>
            <AzureMLTabConnectionFooter
                onNext={handleNext}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={AMLConnectionSteps.Outsourcing}
            />
        </Stack>
    );
};
